/**
 * SidebarProjectRemovalIndicator — the row-level "this is being removed" state.
 *
 * Provider destruction can take several minutes. CMMD persists and resumes the
 * operation, while the catalog overlay keeps this indicator present across
 * reload/navigation. The dialog is not the only source of lifecycle truth.
 *
 * `SidebarProjectItemChrome` has carried `isBusy?: boolean; // opacity-70 + no
 * pointer events (connecting/removing)` since it was written — the removal half
 * of its own documented contract was simply never passed in. This component is
 * the visible other half of finally wiring that up.
 *
 * It is driven by the ROW's removal state, not the dialog's, so it survives the
 * dialog being dismissed and keeps reporting until the operation genuinely
 * settles.
 *
 * Uses the shared quiet-progress treatment: `Loader2Icon`, `animate-spin`,
 * muted micro-type, and a polite live region.
 */

import { CircleAlertIcon, Loader2Icon } from "lucide-react";
import { memo, type MouseEventHandler } from "react";

import { COARSE_POINTER_HIT_AREA_CLASS } from "../../lib/touchTarget";

interface SidebarProjectRemovalIndicatorProps {
  readonly isRemoving: boolean;
  /** The step currently in flight, or `null` before the first is reported. */
  readonly stepLabel: string | null;
  readonly failure?: {
    readonly operationId: string;
    readonly errorCode: string | null;
    readonly errorMessage: string;
  } | null;
  readonly onRetry?: MouseEventHandler<HTMLButtonElement>;
  readonly retryDisabled?: boolean;
}

export const SidebarProjectRemovalIndicator = memo(function SidebarProjectRemovalIndicator({
  isRemoving,
  stepLabel,
  failure = null,
  onRetry,
  retryDisabled = false,
}: SidebarProjectRemovalIndicatorProps) {
  if (!isRemoving && !failure) return null;

  if (failure) {
    const code = failure.errorCode ? ` (${failure.errorCode})` : "";
    const accessibleLabel = `${failure.errorMessage}${code}. Removal receipt: ${failure.operationId}`;
    return (
      <span
        data-slot="project-removal-indicator"
        data-state="retryable"
        data-operation-id={failure.operationId}
        role="status"
        aria-live="polite"
        aria-label={accessibleLabel}
        title={accessibleLabel}
        className="inline-flex shrink-0 items-center gap-1 text-[10px] text-destructive"
      >
        <CircleAlertIcon className="size-3 shrink-0" aria-hidden="true" />
        <span aria-hidden="true">Failed</span>
        {onRetry ? (
          <button
            type="button"
            className={`${COARSE_POINTER_HIT_AREA_CLASS} rounded px-1 underline underline-offset-2 hover:bg-destructive-surface`}
            onClick={onRetry}
            disabled={retryDisabled}
            title={
              retryDisabled
                ? `Retry already running for removal receipt ${failure.operationId}`
                : `Retry removal receipt ${failure.operationId}`
            }
          >
            Retry
          </button>
        ) : null}
      </span>
    );
  }

  // The step name is real detail but a sidebar row is a few pixels wide, so it
  // goes to the tooltip and the accessible name. The VISIBLE word stays
  // "Removing" — an indicator a sighted user cannot see is the bug, not the fix.
  const accessibleLabel = stepLabel ? `Removing — ${stepLabel}` : "Removing…";

  return (
    <span
      data-slot="project-removal-indicator"
      role="status"
      aria-live="polite"
      aria-label={accessibleLabel}
      title={accessibleLabel}
      className="inline-flex shrink-0 items-center gap-1 text-[10px] text-muted-foreground/80"
    >
      <Loader2Icon className="size-3 shrink-0 animate-spin" aria-hidden="true" />
      <span aria-hidden="true">Removing…</span>
    </span>
  );
});
